import { SerialChart } from "@amcharts/amcharts5";
import { FunnelSeries, SlicedChart } from "@amcharts/amcharts5/percent";
import { keys, map, pick } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { transformMetricName } from "@/helpers/charts";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

const VALUE_FIELD_NAME = "value";
const CATEGORY_FIELD_NAME = "category";

export default class extends BaseChart {
  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as SerialChart).series.clear();
    }

    this.createChart();
    this.createSeries();

    this.showLegend(
      map(this.data, CATEGORY_FIELD_NAME) as string[],
      (this.chart as SerialChart).series.values[0].dataItems as LegendData,
      false
    );
  }

  createChart() {
    this.chart = this.root.container.children.push(
      SlicedChart.new(this.root, {
        ...chartInitialSettings,
        layout: this.layout,
      })
    );
  }

  createSeries() {
    const series = (this.chart as SlicedChart).series.push(
      FunnelSeries.new(this.root, {
        name: "seriesName",
        alignLabels: false,
        orientation: "vertical",
        valueField: VALUE_FIELD_NAME,
        categoryField: CATEGORY_FIELD_NAME,
      })
    );

    series.get("colors")?.set("colors", this.palette);

    series.labels.template.setAll({
      ...chartFontSettings.label,
    });

    series.labels.template.adapters.add("text", (text, target) => {
      if (target.dataItem?.dataContext) {
        const { tooltipValues } = target.dataItem?.dataContext as Record<string, Record<string, unknown>>;
        return `${tooltipValues[VALUE_FIELD_NAME]}`;
      }
    });

    series.slices.template.setAll({
      ...chartFontSettings.label,
    });

    series.slices.template.adapters.add("tooltipText", (text, target) => {
      const { tooltipValues, category } = target.dataItem?.dataContext as Record<string, Record<string, unknown>>;
      return `[bold]${category}[/]: ${tooltipValues[VALUE_FIELD_NAME]}`;
    });

    series.ticks.template.setAll({
      visible: false,
      forceHidden: true,
    });

    series.data.setAll(this.data);
  }

  mapData(paramData: ChartDataRecord[]) {
    const data = pick(paramData[0], [
      this.settings.stage1Metric,
      this.settings.stage2Metric,
      this.settings.stage3Metric,
      this.settings.stage4Metric,
      this.settings.stage5Metric,
    ]);

    return keys(data).map((key) => ({
      [CATEGORY_FIELD_NAME]: transformMetricName(key),
      [VALUE_FIELD_NAME]: data[key],
      tooltipValues: {
        [VALUE_FIELD_NAME]: this.settings.compactFormat
          ? this.formatters[key](data[key], { notation: "compact" })
          : this.formatters[key](data[key]),
      },
    }));
  }
}
